import React from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import type { BoardDestination, BoardFeed, BoardRoute, HistoryTick } from './types';
import { ingestHistoryViewHref } from './historyLink';
import { formatCount, laneLabel, relativeTime } from './present';

export type RouteDetailPanelProps = {
  feed?: BoardFeed | null;
  route?: BoardRoute | null;
  destination?: BoardDestination | null;
  ticks?: HistoryTick[];
  ticksLoading?: boolean;
  onClose: () => void;
  now?: number;
};

export default function RouteDetailPanel({
  feed,
  route,
  destination,
  ticks = [],
  ticksLoading = false,
  onClose,
  now,
}: RouteDetailPanelProps) {
  const subject = feed || null;
  const lane = subject?.lane || route?.lane;
  const lastError = subject?.last_error || route?.last_error || null;
  const historyHref = subject?.api_id ? ingestHistoryViewHref(subject.api_id) : '/api-ingest';

  return (
    <aside
      className="w-full max-w-md border-l border-gray-200 bg-white shadow-sm flex flex-col h-full"
      data-testid="df-route-detail"
    >
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            {subject?.name || destination?.label || 'Route'}
          </h2>
          {subject?.endpoint_id && (
            <p className="text-xs text-gray-500 mt-0.5">{subject.endpoint_id}</p>
          )}
          {destination?.kind === 'history_store' && (
            <p className="text-xs text-gray-500 mt-0.5">History store</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 p-1"
          aria-label="Close route detail"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-3 space-y-3 text-sm overflow-y-auto flex-1">
        {lane && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Status</span>
            <span className={`text-xs font-medium ${
              lane === 'live' ? 'text-green-700' : lane === 'broken' ? 'text-red-700' : 'text-gray-600'
            }`}>
              {laneLabel(lane)}
            </span>
          </div>
        )}
        {subject && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Last success</span>
              <span className="text-xs text-gray-800">{relativeTime(subject.last_success_at, now)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wide">24h count</span>
              <span className="text-xs text-gray-800">
                {subject.records_24h == null ? '—' : formatCount(subject.records_24h)}
              </span>
            </div>
            {subject.interval_seconds != null && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Interval</span>
                <span className="text-xs text-gray-800">{subject.interval_seconds}s</span>
              </div>
            )}
          </>
        )}
        {lane === 'broken' && lastError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2" data-testid="df-last-error">
            <div className="text-[10px] uppercase tracking-wide text-red-700 font-semibold mb-1">Last error</div>
            <code className="text-xs text-red-800 break-all">{lastError}</code>
          </div>
        )}
        {destination?.description && (
          <p className="text-xs text-gray-500">{destination.description}</p>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
            Recent history ticks
          </div>
          {ticksLoading ? (
            <p className="text-xs text-gray-500">Loading history…</p>
          ) : ticks.length === 0 ? (
            <p className="text-xs text-gray-500">No rows in api_ingest_history for this feed in this view.</p>
          ) : (
            <ol className="space-y-2" data-testid="df-history-ticks">
              {ticks.map((tick, index) => (
                <li
                  key={tick.ingest_id || `${tick.created_at}-${index}`}
                  className="rounded-md border border-gray-200 px-2 py-1.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-700">{relativeTime(tick.created_at, now)}</span>
                    <span className={`text-[11px] font-medium ${
                      tick.status === 'failed' ? 'text-red-700' : 'text-gray-600'
                    }`}>
                      {tick.status || '—'}
                    </span>
                  </div>
                  {tick.endpoint_id && (
                    <div className="text-[11px] text-gray-500">{tick.endpoint_id}</div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <div className="px-4 py-3 border-t border-gray-100">
        <Link
          to={historyHref}
          data-testid="df-history-deeplink"
          className="inline-flex w-full items-center justify-center h-9 px-3 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
        >
          Open API Ingest History View
        </Link>
      </div>
    </aside>
  );
}
